import { useEffect, useState } from 'react';
import type { Squad, SquadValidation } from '@fantasy/types';
import { api, ApiRequestError } from '../api.ts';

interface SquadLibraryProps {
  onLoad: (squad: Squad, validation: SquadValidation) => void;
  /** Bumped by the parent whenever it creates or imports a squad, so this
   * list refetches and picks it up instead of going stale until the next
   * mount. */
  refreshSignal?: number;
}

/** Squads grouped by gameweek, most recent gameweek first — this is how a
 * manager actually thinks about past squads ("what did I have in GW6"), not
 * as one flat, undifferentiated list. */
function groupByGameweek(squads: Squad[]): [number, Squad[]][] {
  const groups = new Map<number, Squad[]>();
  for (const squad of squads) {
    const group = groups.get(squad.gameweek);
    if (group) group.push(squad);
    else groups.set(squad.gameweek, [squad]);
  }
  return [...groups.entries()].sort(([a], [b]) => b - a);
}

/**
 * A native `<details>` accordion, collapsed by default — not just to save
 * space, but because most of the time there's nothing to gain from opening
 * it. SquadDesk already auto-restores the most recently active squad on
 * mount (see App.tsx), so this list's only real job is reaching a
 * *different* squad — a distinct FPL-linked team, or a separate manual or
 * screenshot build. It is not where gameweek history lives: since T-126/
 * T-127, an FPL-linked squad advances in place rather than adding a row here
 * each gameweek, so this list holds one row per squad *identity*, not one
 * per gameweek. `SquadHistory` (T-129) is the read-only look-back at what a
 * linked squad's own past gameweeks looked like. Before gameweek 1, with at
 * most the one just-imported squad sitting in here, opening it shows nothing
 * the squad view above doesn't already. The count in the summary is there so
 * that "is it worth opening" doesn't itself require opening it.
 *
 * The list itself comes from `GET /api/squads` — this account's squads,
 * scoped server-side to whoever is signed in — not from anything kept in
 * this browser, so it looks the same after signing in on a different device.
 */
export function SquadLibrary({ onLoad, refreshSignal }: SquadLibraryProps) {
  const [squads, setSquads] = useState<Squad[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api
      .listSquads()
      .then(({ squads: loaded }) => {
        setSquads(loaded);
        setError(null);
      })
      .catch((err) => {
        setError(err instanceof ApiRequestError ? err.message : 'Could not load your saved squads.');
      })
      .finally(() => setLoading(false));
  }, [refreshSignal]);

  async function onSelect(id: string) {
    setError(null);
    setLoadingId(id);
    try {
      const { squad, validation } = await api.getSquad(id);
      onLoad(squad, validation);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not load that squad.');
    } finally {
      setLoadingId(null);
    }
  }

  return (
    <details className="panel panel--accent-2 library">
      <summary className="library-summary">
        <span>
          <span className="library-chevron" aria-hidden="true">
            ▸
          </span>
          Saved squads
        </span>
        <span className="library-summary-count hint hint--inline">
          {loading
            ? 'loading…'
            : squads.length === 0
              ? 'empty'
              : squads.length === 1
                ? '1 squad'
                : `${squads.length} squads`}
        </span>
      </summary>

      <div className="library-body">
        {loading ? (
          <p className="hint">Loading your saved squads…</p>
        ) : squads.length === 0 && !error ? (
          <p className="hint">Nothing saved yet — import or save a squad and it will show up here.</p>
        ) : (
          groupByGameweek(squads).map(([gameweek, group]) => (
            <div className="library-group" key={gameweek}>
              <h4>Gameweek {gameweek}</h4>
              <ul className="list-reset">
                {group.map((entry) => (
                  <li className="library-entry" key={entry.id}>
                    <div>
                      <button
                        type="button"
                        className="ghost"
                        onClick={() => onSelect(entry.id)}
                        disabled={loadingId === entry.id}
                        aria-busy={loadingId === entry.id}
                      >
                        {loadingId === entry.id && <span className="spinner" aria-hidden="true" />}
                        {loadingId === entry.id ? 'Loading…' : entry.name}
                      </button>
                      <div className="library-entry-date">
                        saved {new Date(entry.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}

        {error && <p className="hint text-bad">{error}</p>}
      </div>
    </details>
  );
}
